import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useFormik } from "formik";
import {
  CreatePatientModalProps,
  CreatePatientFormData,
  PATIENT_FIELD_PLACEHOLDERS,
  PATIENT_MODAL_TEXTS,
  PATIENT_MODAL_STYLES,
} from "../../../utils/constants";
import {
  createPatientValidationSchema,
  initialPatientFormValues,
  sanitizePatientData,
} from "../../../utils/validationSchemas";

const CreatePatientModal: React.FC<CreatePatientModalProps> = ({
  open,
  onClose,
  onPatientCreated,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Configuración de Formik con validación
  const formik = useFormik<CreatePatientFormData>({
    initialValues: initialPatientFormValues,
    validationSchema: createPatientValidationSchema,
    onSubmit: async (values) => {
      if (onSubmit) {
        setIsSubmitting(true);
        try {
          const sanitizedData = sanitizePatientData(values);
          await onSubmit(sanitizedData);
          onPatientCreated?.(sanitizedData);
          handleClose();
        } catch (error) {
          console.error("Error creating patient:", error);
          // El error se maneja en el componente padre
        } finally {
          setIsSubmitting(false);
        }
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          width: 500,
          maxWidth: "90vw",
          minHeight: 400,
        },
        "& .MuiBackdrop-root": {
          backgroundColor: PATIENT_MODAL_STYLES.overlayColor,
        },
      }}
    >
      {/* Título del Modal */}
      <DialogTitle
        sx={{
          color: "#333",
          textAlign: "center",
          padding: "20px 24px 16px",
          position: "relative",
          backgroundColor: "white",
        }}
      >
        <Typography variant="h6" component="h2" fontWeight={600}>
          {PATIENT_MODAL_TEXTS.CREATE_TITLE}
        </Typography>

        {/* Botón de cerrar */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Contenido del Modal */}
      <DialogContent sx={{ padding: "0 24px", backgroundColor: "white" }}>
        <Box component="form" onSubmit={formik.handleSubmit}>
          {/* Campo Nombre/s */}
          <TextField
            fullWidth
            name="firstName"
            label="Nombre/s"
            placeholder={PATIENT_FIELD_PLACEHOLDERS.FIRST_NAME}
            variant="outlined"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
            margin="normal"
            sx={{
              "& .MuiHelperText-root.Mui-error": {
                color: PATIENT_MODAL_STYLES.errorColor,
                fontSize: "0.875rem",
                marginLeft: 0,
              },
            }}
          />

          {/* Campo Apellidos */}
          <TextField
            fullWidth
            name="lastName"
            label="Apellidos"
            placeholder={PATIENT_FIELD_PLACEHOLDERS.LAST_NAME}
            variant="outlined"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
            margin="normal"
            sx={{
              "& .MuiHelperText-root.Mui-error": {
                color: PATIENT_MODAL_STYLES.errorColor,
                fontSize: "0.875rem",
                marginLeft: 0,
              },
            }}
          />

          {/* Campo Correo Electrónico */}
          <TextField
            fullWidth
            name="email"
            label="Correo electrónico"
            placeholder={PATIENT_FIELD_PLACEHOLDERS.EMAIL}
            variant="outlined"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            margin="normal"
            sx={{
              "& .MuiHelperText-root.Mui-error": {
                color: PATIENT_MODAL_STYLES.errorColor,
                fontSize: "0.875rem",
                marginLeft: 0,
              },
            }}
          />
        </Box>
      </DialogContent>

      {/* Botones de Acción */}
      <DialogActions
        sx={{
          padding: "16px 24px 24px",
          gap: 2,
          justifyContent: "center",
          backgroundColor: "white",
        }}
      >
        {/* Botón Cancelar */}
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: PATIENT_MODAL_STYLES.secondaryButtonColor,
            color: (theme) => theme.palette.text.primary,
            "&:hover": {
              borderColor: (theme) => theme.palette.grey[400],
              backgroundColor: (theme) => theme.palette.grey[50],
            },
            minWidth: 120,
          }}
          disabled={isSubmitting}
        >
          {PATIENT_MODAL_TEXTS.CANCEL_BUTTON}
        </Button>

        {/* Botón Crear Paciente */}
        <Button
          variant="contained"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || isSubmitting}
          sx={{
            backgroundColor: PATIENT_MODAL_STYLES.primaryButtonColor,
            color: "#333",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#c8d661", // Versión más oscura del amarillo/verde
            },
            "&.Mui-disabled": {
              backgroundColor: (theme) => theme.palette.grey[300],
              color: (theme) => theme.palette.grey[500],
            },
            minWidth: 140,
          }}
        >
          {isSubmitting
            ? PATIENT_MODAL_TEXTS.SUBMITTING_TEXT
            : PATIENT_MODAL_TEXTS.SUBMIT_BUTTON}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePatientModal;
